/**
 * SectionLibraryCard Component
 *
 * Individual section card in the Section Library browser.
 * Displays section title, description, scope indicator, example output preview,
 * and an "Add to Template" button.
 */

import React, { useState } from 'react';
import {
  ScopeBadge,
  TemplateSectionCard,
  TemplateSectionContent,
} from '@components/TemplateSections';
import { Add as AddIcon, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import type { SectionLibraryWithMeta } from '../types';

interface SectionLibraryCardProps {
  /** Section data with metadata */
  section: SectionLibraryWithMeta;
  /** Callback when user clicks "Add to Template" */
  onAddToTemplate: (section: SectionLibraryWithMeta) => void;
}

export const SectionLibraryCard: React.FC<SectionLibraryCardProps> = ({
  section,
  onAddToTemplate,
}) => {
  const theme = useTheme();
  const [showPreview, setShowPreview] = useState(false);

  const hasExampleOutput = Boolean(section.section.example_output);

  return (
    <TemplateSectionCard variant="card">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Header with title, scope icon, and Add button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {section.section.title}
              </Typography>
              <ScopeBadge scope={section.section.owner_scope} />
            </Box>
            {section.section.description && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                {section.section.description}
              </Typography>
            )}
          </Box>

          {/* Add Button */}
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => onAddToTemplate(section)}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              flexShrink: 0,
            }}
          >
            Add
          </Button>
        </Box>

        {/* Example Output Preview */}
        {hasExampleOutput && (
          <Box>
            <Button
              variant="text"
              size="small"
              onClick={() => setShowPreview(!showPreview)}
              endIcon={
                <ExpandMore
                  sx={{
                    transform: showPreview ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: theme.transitions.create('transform', {
                      duration: theme.transitions.duration.short,
                    }),
                  }}
                />
              }
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                fontWeight: 500,
                p: 0,
                minWidth: 'auto',
              }}
            >
              Preview Example Output
            </Button>
            <Box
              sx={{
                mt: 1,
                p: 2,
                bgcolor: 'background.default',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <TemplateSectionContent
                markdown={section.section.example_output}
                collapsed={!showPreview}
                isExample={true}
              />
            </Box>
          </Box>
        )}

        {/* Categories */}
        {section.section.categories && section.section.categories.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {section.section.categories.map((category: string, index: number) => (
              <Chip
                key={index}
                label={category}
                size="small"
                variant="outlined"
                sx={{ fontSize: '0.7rem', height: 20 }}
              />
            ))}
          </Box>
        )}
      </Box>
    </TemplateSectionCard>
  );
};

